import { useState, useEffect, createContext } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  getMainView,
  getViewerView,
  getSideViewerView,
  callApi,
  setCookie,
  clearCookie,
} from "./Helpers";
import { useParams } from "react-router-dom";
import Viewer from "./components/Viewer";
import MainBody from "./components/MainBody";
import SideViewer from "./components/SideViewer";
import ConfirmDialog from "./components/ConfirmDialog";
import { ToastContainer, toast } from "react-toastify";
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import OverlayLoader from "./components/OverlayLoader";
import MainLoader from "./components/MainLoader";
import Backdrop from "./components/Backdrop";

export const AppContext = createContext(null);

export default function App(props) {
  const [ready, setReady] = useState(false); //for checking if app is ready

  const { _navItem, _navSubItem, _navExtraItem, _navMoreItem } = useParams(); //for routing purposes

  const [navItem, setNavItem] = useState(_navItem); //routing
  const [navSubItem, setNavSubItem] = useState(_navSubItem); //routing
  const [navExtraItem, setNavExtraItem] = useState(_navExtraItem); //routing
  const [navMoreItem, setNavMoreItem] = useState(_navMoreItem); //routing

  const [showViewer, setShowViewer] = useState(false); //controlling the display of Viewer component
  const [viewerView, setViewerView] = useState(null); //the view to be shown in viewer

  const [showSideViewer, setShowSideViewer] = useState(false); //controlling the display of SideViewer component
  const [sideViewerView, setSideViewerView] = useState(null); //the view to be shown in viewer

  const [mainView, setMainView] = useState(null); //the view tobe shown in MainBody

  const [showOverlayLoader, setShowOverlayLoader] = useState(false); //controlling the display of OverlayLoader

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState("");

  const [isSideBarOpen, setIsSideBarOpen] = useState(false); //controls open and close for sidebar in mobile devices

  const [auth, setAuth] = useState(false); //track user authorization status

  //currentSession
  const [currentUserData, setCurrentUserData] = useState(null);

  //App level data
  const [systemParams, setSystemParams] = useState(null);

  const [allNews, setAllNews] = useState(null);
  const [allReports, setAllReports] = useState(null);
  const [allEvents, setAllEvents] = useState(null);
  const [allGallery, setAllGallery] = useState(null);
  const [allProducts, setAllProducts] = useState(null);
  const [allResearch, setAllResearch] = useState(null);
  const [allEthics, setAllEthics] = useState(null);
  const [allPublications, setAllPublications] = useState(null);
  const [allJobPosts, setAllJobPosts] = useState(null);
  const [allThreads, setAllThreads] = useState(null);
  const [allBanners, setAllBanners] = useState(null);

  async function init() {
    /**
     * Initialize the app here
     */

    setReady(false);

    await authCheck().then(async (_auth) => {
      if (_auth) {
        await getCurrentUserData();
      }
    });

    setReady(true);
  }

  async function getSystemParams() {
    return new Promise(async (resolve) => {
      await callApi("get-system-params", {}).then((response) => {
        if (response.status === 1) {
          setSystemParams(response.data);
          resolve(response.data);
        } else {
          resolve(null);
        }
      });
    });
  }

  function navBack() {
    window.history.back();
    setShowOverlayLoader(false);
  }

  function tellError(msg) {
    toast.error(msg);
  }

  function tellInfo(msg) {
    toast.info(msg);
  }

  function tellWarning(msg) {
    toast.warn(msg);
  }

  function tellMessage(msg) {
    toast.success(msg);
  }

  function openSideBar() {
    appContext.setIsSideBarOpen(true);
  }

  function closeSideBar() {
    appContext.setIsSideBarOpen(false);
  }

  function refresh() {
    /**
     * This function refreshes the whole app
     */
    window.location.reload(); //remember to optimize
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function navTo(nav) {
    /**
     * This function handles navigation inside the app
     * Utilizing Hash based routing
     * nav is the object supporting the following keys: item, subItem, extraItem, moreItem
     */
    if (nav) {
      //..

      let url = "";
      if (nav.item) {
        url = `#/${nav.item}/`;
      }

      if (nav.subItem) {
        url += `${nav.subItem}/`;
      }

      if (nav.extraItem) {
        url += `${nav.extraItem}/`;
      }

      if (nav.moreItem) {
        url += `${nav.moreItem}/`;
      }

      window.location.href = url;
      //..
    }
  }

  async function getCurrentUserData() {
    return new Promise(async (resolve) => {
      await callApi("get-current-user-data", {}).then((response) => {
        if (response.status === 1) {
          setCurrentUserData(response.data);
          setAuth(true);
          resolve(response.data);
        } else {
          setCurrentUserData(null);
          setAuth(false);
          resolve(null);
        }
      });
    });
  }

  function authCheck() {
    return new Promise(async (resolve) => {
      await callApi("auth-check", {}).then((response) => {
        if (response.status === 1) {
          setAuth(true);
          resolve(true);
        } else {
          setAuth(false);
          resolve(false);
        }
      });
    });
  }

  function loginMember({ email, password }) {
    return new Promise(async (resolve) => {
      await callApi("password-login", {
        email,
        password,
      })
        .then(async (response) => {
          const resolvedUuid = response?.user?.uuid || response?.user?.userUuid;

          if (response.status === 1 && resolvedUuid && response.user?.token) {
            setCookie("userUuid", resolvedUuid, 7);
            setCookie("uuid", resolvedUuid, 7);
            setCookie("userToken", response.user.token, 7);
            setCookie("userType", "member", 7);
            setAuth(true);
            await getCurrentUserData();
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(() => resolve(false));
    });
  }

  function registerMember(payload) {
    return new Promise(async (resolve) => {
      await callApi("register-user", payload)
        .then(async (response) => {
          const resolvedUuid = response?.user?.uuid || response?.user?.userUuid;

          if (response.status === 1 && resolvedUuid && response.user?.token) {
            setCookie("userUuid", resolvedUuid, 7);
            setCookie("uuid", resolvedUuid, 7);
            setCookie("userToken", response.user.token, 7);
            setCookie("userType", "member", 7);
            setAuth(true);
            await getCurrentUserData();
            resolve(true, response);
          } else {
            resolve(false, response);
          }
        })
        .catch(() => resolve(false));
    });
  }

  function logoutMember() {
    clearCookie("userUuid");
    clearCookie("uuid");
    clearCookie("userToken");
    clearCookie("userType");
    setCurrentUserData(null);
    setAuth(false);
    return true;
  }

  function getMyMembershipApplications() {
    return new Promise(async (resolve) => {
      await callApi("get-my-membership-applications", {})
        .then((response) => {
          if (response.status === 1) {
            resolve(Array.isArray(response.data) ? response.data : []);
          } else {
            resolve([]);
          }
        })
        .catch(() => resolve([]));
    });
  }

  const changeLanguage = (lang) => {
    const applyLanguage = (attempt = 0) => {
      const element = document.querySelector(".goog-te-combo");

      if (element) {
        element.value = lang;
        element.dispatchEvent(new Event("change"));
        setCookie("language", lang, 30);
        return;
      }

      if (attempt < 10) {
        window.setTimeout(() => applyLanguage(attempt + 1), 300);
      }
    };

    applyLanguage();
  };

  function createMembershipApplication(payload) {
    return new Promise(async (resolve) => {
      await callApi("create-membership-application", payload)
        .then((response) => {
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  function activateDialog(params) {
    let { message, onConfirm } = params;
    setConfirmDialogAction(() => {
      return onConfirm;
    });
    setConfirmDialogMessage(message);
    setShowConfirmDialog(true);
  }

  function getPageParams({ limit = 10, offset = 0, withMeta = false } = {}) {
    const parsedLimit = Number.parseInt(limit, 10);
    const parsedOffset = Number.parseInt(offset, 10);

    return {
      limit: Number.isInteger(parsedLimit) && parsedLimit > 0 ? parsedLimit : 10,
      offset:
        Number.isInteger(parsedOffset) && parsedOffset > 0 ? parsedOffset : 0,
      withMeta,
    };
  }

  function getPagedData(response, limit, withMeta) {
    const rows = response.status === 1 && Array.isArray(response.data)
      ? response.data
      : [];
    const records = withMeta ? rows.slice(0, limit) : rows;

    return {
      records,
      hasMore: withMeta && rows.length > limit,
    };
  }

  function getAllNews() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-news", {}).then((response) => {
        if (response.status === 1) {
          setAllNews(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllJobPosts() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-jobposts", {}).then((response) => {
        if (response.status === 1) {
          setAllJobPosts(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllBanners() {
    return new Promise(async (resolve) => {
      await callApi("get-all-banners", {}).then((response) => {
        if (response.status === 1) {
          const banners = Array.isArray(response.data) ? response.data : [];
          setAllBanners(banners);
          resolve(banners);
        } else {
          setAllBanners([]);
          resolve([]);
        }
      });
    });
  }

  function subscribe(email) {
    return new Promise(async (resolve, reject) => {
      await callApi("create-subscriber", email)
        .then((response) => {
          if (response.status === 1) {
            resolve(response.data);
          } else {
            resolve(null);
          }
        })
        .catch(() => {
          resolve(null);
        });
    });
  }

  function createContactMessage(payload) {
    return new Promise(async (resolve) => {
      await callApi("create-messages", payload).then((response) => {
        resolve(response);
      });
    });
  }

  function getSubscriptionStatus() {
    return new Promise(async (resolve) => {
      await callApi("get-subscription-status", {})
        .then((response) => {
          if (response.status === 1) {
            resolve(response.subscriptionStatus || "Not Subscribed");
          } else {
            resolve("Not Subscribed");
          }
        })
        .catch(() => resolve("Not Subscribed"));
    });
  }

  function unsubscribe() {
    return new Promise(async (resolve, reject) => {
      await callApi("unsubscribe").then((response) => {
        if (response.status === 1) {
          resolve(response.data);
        }
      });
    });
  }

  function getAllPublications() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-publications", {}).then((response) => {
        if (response.status === 1) {
          const publications = Array.isArray(response.data)
            ? response.data
            : [];
          setAllPublications(publications);
          resolve(publications);
        } else {
          setAllPublications([]);
          resolve([]);
        }
      });
    });
  }

  function getAllReports() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-resources-per-category", {
        categories: "Reports",
      }).then((response) => {
        if (response.status === 1) {
          setAllReports(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllResearch() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-resources-per-category", {
        categories: "Research",
      }).then((response) => {
        if (response.status === 1) {
          setAllResearch(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllEthics() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-resources-per-category", {
        categories: "Ethics Guidelines",
      }).then((response) => {
        if (response.status === 1) {
          setAllEthics(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllEvents() {
    return new Promise(async (resolve, reject) => {
      await callApi("get-all-events", {}).then((response) => {
        if (response.status === 1) {
          setAllEvents(response.data);
          resolve(response.data);
        }
      });
    });
  }

  function getAllGallery(params) {
    return new Promise(async (resolve, reject) => {
      const page = getPageParams(params);
      await callApi("get-all-gallery", {
        limit: page.withMeta ? page.limit + 1 : page.limit,
        offset: page.offset,
      }).then((response) => {
        const { records, hasMore } = getPagedData(
          response,
          page.limit,
          page.withMeta,
        );

        if (page.limit >= 10 && page.offset === 0) {
          setAllGallery(records);
        }

        resolve(page.withMeta ? { records, hasMore } : records);
      });
    });
  }

  function getAllProducts(params) {
    return new Promise(async (resolve) => {
      const page = getPageParams(params);
      await callApi("get-all-products", {
        limit: page.withMeta ? page.limit + 1 : page.limit,
        offset: page.offset,
      }).then((response) => {
        const { records, hasMore } = getPagedData(
          response,
          page.limit,
          page.withMeta,
        );

        if (page.limit >= 10 && page.offset === 0) {
          setAllProducts(records);
        }

        resolve(page.withMeta ? { records, hasMore } : records);
      });
    });
  }

  function getAllThreads() {
    return new Promise(async (resolve) => {
      await callApi("get-all-threads", {})
        .then((response) => {
          if (response.status === 1) {
            const threads = Array.isArray(response.data) ? response.data : [];
            setAllThreads(threads);
            resolve(threads);
          } else {
            setAllThreads([]);
            resolve([]);
          }
        })
        .catch(() => {
          setAllThreads([]);
          resolve([]);
        });
    });
  }

  function getThreadData(threadUuid) {
    return new Promise(async (resolve) => {
      await callApi("get-thread-data", { threadUuid })
        .then((response) => {
          if (response.status === 1) {
            resolve(response.data);
          } else {
            resolve(null);
          }
        })
        .catch(() => resolve(null));
    });
  }

  function getThreadMessages(threadUuid) {
    return new Promise(async (resolve) => {
      await callApi("get-thread-messages", { threadUuid })
        .then((response) => {
          if (response.status === 1) {
            resolve(Array.isArray(response.data) ? response.data : []);
          } else {
            resolve([]);
          }
        })
        .catch(() => resolve([]));
    });
  }

  function createThread(payload) {
    return new Promise(async (resolve) => {
      await callApi("create-thread", payload)
        .then(async (response) => {
          if (response.status === 1) {
            await getAllThreads();
          }
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  function updateThread(payload) {
    return new Promise(async (resolve) => {
      await callApi("update-thread", {
        threadUuid: payload.uuid,
        title: payload.title,
        body: payload.body,
      })
        .then(async (response) => {
          if (response.status === 1) {
            await getAllThreads();
          }
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  function deleteThread(payload) {
    return new Promise(async (resolve) => {
      await callApi("delete-thread", {
        threadUuid: payload.uuid,
      })
        .then(async (response) => {
          if (response.status === 1) {
            await getAllThreads();
          }
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  function createThreadMessage(payload) {
    return new Promise(async (resolve) => {
      await callApi("create-thread-message", payload)
        .then((response) => {
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  function updateThreadMessage(payload) {
    return new Promise(async (resolve) => {
      await callApi("update-thread-message", {
        messageUuid: payload.uuid,
        body: payload.body,
      })
        .then((response) => {
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  function deleteThreadMessage(payload) {
    return new Promise(async (resolve) => {
      await callApi("delete-thread-message", {
        messageUuid: payload.uuid,
      })
        .then((response) => {
          resolve(response);
        })
        .catch(() => {
          resolve({
            status: 0,
            msg: "Network Connection Error",
          });
        });
    });
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 40,
    });
  }, []);

  useEffect(() => {
    if (isSideBarOpen) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
  }, [isSideBarOpen]);

  useEffect(() => {
    setNavItem(_navItem);
    setNavSubItem(_navSubItem);
    setNavExtraItem(_navExtraItem);
    setNavMoreItem(_navMoreItem);
  }, [_navItem, _navSubItem, _navExtraItem, _navMoreItem]);

  useEffect(() => {
    AOS.refresh();
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  useEffect(() => {
    if (currentUserData) {
      //handle current user data changes here
    }
  }, [currentUserData]);

  useEffect(() => {
    //check for viewers
    if (navItem === "view") {
      //activate viewer
      setShowViewer(true);
      setViewerView(getViewerView(appContext));

      //hide other viewers
      setShowSideViewer(false);
      setSideViewerView(null);
    } else if (navItem === "side-view") {
      //activate viewer
      setShowSideViewer(true);
      setSideViewerView(getSideViewerView(appContext));

      //hide other viewers
      setShowViewer(false);
      setViewerView(null);
    } else {
      //just set normal views

      setShowViewer(false);
      setShowSideViewer(false);

      setViewerView(null);
      setSideViewerView(null);

      const _mainView = getMainView(appContext);
      if (_mainView) {
        setMainView(_mainView);
      }

      if (!(!appContext.navItem || appContext.navItem === "home")) {
        setTimeout(() => {
          scrollToTop(); //trigger top scrolling
        }, 200);
      }
    }
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  const appContext = {
    refresh,
    navTo,
    mainView,
    viewerView,
    setShowOverlayLoader,
    navItem,
    navSubItem,
    navExtraItem,
    navMoreItem,
    setShowViewer,
    showViewer,
    navBack,
    showSideViewer,
    setShowSideViewer,
    sideViewerView,
    activateDialog,
    setShowConfirmDialog,
    confirmDialogAction,
    confirmDialogMessage,
    showConfirmDialog,
    tellError,
    tellInfo,
    tellMessage,
    tellWarning,
    isSideBarOpen,
    setIsSideBarOpen,
    showOverlayLoader,
    auth,
    currentUserData,
    systemParams,
    getSystemParams,
    authCheck,
    getCurrentUserData,
    loginMember,
    registerMember,
    logoutMember,
    getMyMembershipApplications,
    createMembershipApplication,
    changeLanguage,
    openSideBar,
    closeSideBar,
    scrollToTop,
    allNews,
    getAllNews,
    allEvents,
    getAllEvents,
    allGallery,
    getAllGallery,
    allProducts,
    getAllProducts,
    allResearch,
    getAllResearch,
    allEthics,
    getAllEthics,
    allReports,
    getAllReports,
    allPublications,
    getAllPublications,
    allJobPosts,
    getAllJobPosts,
    allBanners,
    getAllBanners,
    allThreads,
    getAllThreads,
    getThreadData,
    getThreadMessages,
    createThread,
    updateThread,
    deleteThread,
    createThreadMessage,
    updateThreadMessage,
    deleteThreadMessage,
    createContactMessage,
    subscribe,
    unsubscribe,
    getSubscriptionStatus,
  };

  if (ready) {
    return (
      <AppContext.Provider value={appContext}>
        <MainBody />
        <TopBar />
        <SideBar />
        <Backdrop />
        <Viewer />
        <SideViewer />
        <ConfirmDialog />
        <OverlayLoader />
        <ToastContainer
          position="bottom-right"
          autoClose={3200}
          hideProgressBar
          closeButton={false}
          toastClassName="modern-toast"
        />
      </AppContext.Provider>
    );
  } else {
    return (
      <AppContext.Provider value={appContext}>
        <ToastContainer
          position="bottom-right"
          autoClose={3200}
          hideProgressBar
          closeButton={false}
          toastClassName="modern-toast"
        />
        <MainLoader />
      </AppContext.Provider>
    );
  }
}
